/**
 * 55 — the lyric vetting desk.
 *
 * When a NEW take is transcribed against a LOCKED master and its words diverge,
 * the Worker opens a flag. This is where Brad (or Johhny) rules on each one:
 * did the machine mishear, did it hallucinate, or did the SONG actually change?
 *
 * Two surfaces:
 *   · a notice banner, shown on load to Brad/Johhny when anything is waiting —
 *     the "lyrics need to be vetted" the user asked for;
 *   · a review sheet the banner opens: every open flag, the machine-vs-master
 *     diff, and the three rulings. 'Song changed' sends Brad to the song to edit
 *     and re-lock; the other two just clear the flag (the words were right).
 *
 * Only ever shown to the two lyric owners — everyone else's getLyricReviews()
 * comes back empty (403 → []), so the banner never appears for them.
 */

import { getLyricReviews, resolveLyricFlag, ApiError, type LyricReview, type Member } from './api';
import { h, contentSheet, toast, fmtTime, relDate } from './ui';

export function isLyricOwner(me: Member | null | undefined): boolean {
  return !!me && (me.id === 'brad' || me.id === 'johhny' || me.role === 'admin');
}

const svgFlag = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 21V4M5 4h11l-2 4 2 4H5"/></svg>`;

let bannerEl: HTMLElement | null = null;

/**
 * Check for waiting flags and, if the current member owns the words and any are
 * open, drop a tappable notice banner at the top of the app. Safe to call on
 * every boot; it removes any prior banner first.
 */
export async function checkVetting(me: Member | null, go: (hash: string) => void): Promise<void> {
  clearBanner();
  if (!isLyricOwner(me)) return;
  let reviews: LyricReview[];
  try {
    reviews = await getLyricReviews();
  } catch {
    return; // never let a background check break boot
  }
  if (reviews.length === 0) return;
  showBanner(reviews.length, () => void openVetSheet(go));
}

function clearBanner(): void {
  bannerEl?.remove();
  bannerEl = null;
}

function showBanner(count: number, onOpen: () => void): void {
  clearBanner();
  const label = `${count} ${count === 1 ? 'take needs' : 'takes need'} lyric vetting`;
  const banner = h('button', { class: 'vet-banner', 'aria-label': label }, [
    h('span', { class: 'vet-banner-ic', html: svgFlag }),
    h('span', { class: 'vet-banner-txt', text: label }),
    h('span', { class: 'vet-banner-go', text: 'Review' }),
  ]);
  banner.addEventListener('click', onOpen);
  document.getElementById('app')?.appendChild(banner);
  bannerEl = banner;
}

/** Open the review sheet. Fetches fresh, so it's current even if the banner is stale. */
export async function openVetSheet(go: (hash: string) => void): Promise<void> {
  let reviews: LyricReview[];
  try {
    reviews = await getLyricReviews();
  } catch (e) {
    toast(e instanceof ApiError ? e.human : 'Couldn’t load the vetting list.', true);
    return;
  }

  const list = h('div', { class: 'vet-list' });
  const close = contentSheet('Lyrics to vet', list);

  const render = () => {
    list.replaceChildren();
    if (reviews.length === 0) {
      list.appendChild(
        h('p', { class: 'vet-empty', text: 'All caught up — nothing to vet.' }),
      );
      clearBanner();
      return;
    }
    list.appendChild(
      h('p', {
        class: 'vet-lead',
        text: 'A new take’s words differ from the locked lyrics. For each, tell me why.',
      }),
    );
    for (const r of reviews) list.appendChild(reviewCard(r));
  };

  function reviewCard(r: LyricReview): HTMLElement {
    const card = h('div', { class: 'vet-card' });
    card.appendChild(
      h('div', { class: 'vet-card-h' }, [
        h('span', { class: 'vet-song', text: r.song_title }),
        h('span', {
          class: 'vet-take',
          text: `${r.version_name || 'a take'}${r.recorded_at ? ` · ${relDate(r.recorded_at) || ''}` : ''}`,
        }),
      ]),
    );

    // the machine-vs-master diff: what Brad wrote vs what Whisper heard, timed.
    const diff = h('div', { class: 'vet-diff' });
    for (const d of r.diff.slice(0, 8)) {
      diff.appendChild(
        h('div', { class: 'vet-diffrow' }, [
          h('span', { class: 'vet-at', text: fmtTime(d.start_s) }),
          h('div', { class: 'vet-words' }, [
            h('span', { class: 'vet-master', text: d.master || '—' }),
            h('span', { class: 'vet-heard', text: d.heard ? `heard: ${d.heard}` : 'heard: (nothing clear)' }),
          ]),
        ]),
      );
    }
    if (r.diff.length > 8) diff.appendChild(h('div', { class: 'vet-more', text: `+${r.diff.length - 8} more lines` }));
    card.appendChild(diff);

    // the three rulings
    const act = (resolution: 'hallucination' | 'hard_to_hear' | 'lyric_change', label: string, accent = false) => {
      const b = h('button', { class: `btn-sm vet-act${accent ? ' primary' : ''}`, text: label });
      b.addEventListener('click', async () => {
        try {
          await resolveLyricFlag(r.id, resolution);
        } catch (e) {
          toast(e instanceof ApiError ? e.human : 'Couldn’t save that. Try again?', true);
          return;
        }
        reviews = reviews.filter((x) => x.id !== r.id);
        render();
        if (resolution === 'lyric_change') {
          // The song genuinely changed — take Brad to it to fix + re-lock.
          close();
          go(`#/song/${encodeURIComponent(r.song_slug || r.song_id)}`);
        } else {
          toast('Sorted.');
        }
      });
      return b;
    };
    card.appendChild(
      h('div', { class: 'vet-acts' }, [
        act('lyric_change', 'Song changed — fix it', true),
        act('hard_to_hear', 'Just hard to hear'),
        act('hallucination', 'Machine made it up'),
      ]),
    );
    return card;
  }

  render();
}
